export type InformationNews = {
  eid: number;
  season: number;
  text: string;
  tids?: number[];
  type: string;
};

export type InformationSourceFilter = "all" | "league" | "owner" | "team";
export type InformationNewsSource = "league" | "team";

export type InformationMessage = {
  from?: string;
};

export const getInformationMessageSource = (
  message: InformationMessage,
): Exclude<InformationSourceFilter, "all"> => {
  const normalizedFrom = message.from?.trim().toLowerCase() ?? "";
  if (normalizedFrom.includes("owner")) {
    return "owner";
  }
  if (normalizedFrom.includes("league")) {
    return "league";
  }
  return "team";
};

export const filterInformationMessages = <Message extends InformationMessage>({
  messages,
  source,
}: {
  messages: Message[];
  source: InformationSourceFilter;
}) =>
  source === "all"
    ? messages
    : messages.filter((message) => getInformationMessageSource(message) === source);

export const getInformationNewsSource = (
  news: Pick<InformationNews, "tids">,
  userTid?: number,
): InformationNewsSource =>
  userTid !== undefined && news.tids?.includes(userTid) ? "team" : "league";

export const filterInformationNews = ({
  news,
  source,
  userTid,
}: {
  news: InformationNews[];
  source: InformationSourceFilter;
  userTid?: number;
}) => {
  if (source === "owner") {
    return [];
  }
  if (source === "all") {
    return news;
  }
  return news.filter((item) => getInformationNewsSource(item, userTid) === source);
};

export const getInformationSourceCounts = ({
  messageCount,
  messages,
  news,
  userTid,
}: {
  messageCount?: number;
  messages?: InformationMessage[];
  news: InformationNews[];
  userTid?: number;
}) => {
  const messageItems =
    messages ?? Array.from({ length: messageCount ?? 0 }, () => ({ from: "The Owner" }));
  let owner = 0;
  let team = 0;
  let league = 0;
  for (const message of messageItems) {
    const source = getInformationMessageSource(message);
    if (source === "owner") {
      owner += 1;
    } else if (source === "team") {
      team += 1;
    } else {
      league += 1;
    }
  }
  for (const item of news) {
    if (getInformationNewsSource(item, userTid) === "team") {
      team += 1;
    } else {
      league += 1;
    }
  }

  return {
    all: messageItems.length + news.length,
    league,
    owner,
    team,
  };
};

export const getInformationNewsExcerpt = (text: string, maxLength = 120) => {
  const plainText = text
    .replace(/<br\s*\/?\s*>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();

  return plainText.length > maxLength
    ? `${plainText.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
    : plainText;
};
